/**
 * UnreadBanner — bottom-of-list overlay that doubles as:
 *   1. An unread-summary pill when peer messages arrived while the user was
 *      scrolled away from the bottom. The pill shows a single-line preview of
 *      the most recent unread peer message and, when tapped, scrolls the list
 *      back to the bottom (which also marks the conversation seen).
 *   2. A round "scroll-to-bottom" floating button (`ScrolledUpButton`) when
 *      there are no unread peer messages but the user is scrolled up.
 *
 * Flutter source of truth: `unreadMessageWidget`
 * (`chat_screen.dart:3380-3459`). Flutter conflates both roles into a single
 * widget — when `_unreadMessageReceived && content != ''` it renders the
 * pill, otherwise (when `_isScrolledUp`) it renders `ScrolledUpWidget`.
 * The bundled web port preserves that single call-site contract.
 *
 * Visibility predicate (`chat_screen.dart:3399-3400`):
 *   `(_unreadMessageReceived && content != '') || _isScrolledUp`
 * On the web that becomes:
 *   `(unreadBannerVisible && unreadPreview != null) || isScrolledUp`.
 * `unreadBannerVisible` is the transient 3 s flag — sourced from
 * `useChat`, flipped on every peer-message-while-scrolled-away arrival
 * (`chat_screen.dart:566-578`). When both branches are false this
 * component renders `null` (the caller does not need to gate the mount).
 *
 * Preview-text format (`chat_screen.dart:3380-3395`):
 *  - `ChatMessageType.gift`  → `l10n.chat.unread.gift`   ({peerFirstName})
 *  - `ChatMessageType.text`  → `lastUnreadItem.content`  (raw message body)
 *  - `ChatMessageType.image` → `l10n.chat.unread.photo`  ({peerFirstName})
 *  - `ChatMessageType.video` → `l10n.chat.unread.video`  ({peerFirstName})
 *  - other types → no preview (the Flutter chain has no fallback branch)
 *
 * Callers build the preview string via
 * `useExpauseLocalizations().expauseLocalizations.getUnreadPreview(message,
 * peerFirstName)` — the helper lives there because it is a
 * value-conditional translation lookup keyed off `ChatMessageType`.
 *
 * Deviations from Flutter UI (allowed per `presentation.md`):
 *  - The Flutter `Padding` controlling vertical placement
 *    (`bottomNavigationBarHeight + viewPadding.bottom + 24`,
 *    `chat_screen.dart:3402-3406`) is the *page's* responsibility on web; the
 *    chat page positions this component above the composer. This component
 *    only owns the pill / ScrolledUpButton shell and centers itself
 *    horizontally.
 *  - `maxWidth` mirrors Flutter's `MediaQuery.of(context).size.width / 2`
 *    (chat_screen.dart:3417) via CSS `max-width: 50vw`.
 *  - Pill corner radius uses `responsive.borderRadius.lg` (= 16 on mobile;
 *    Flutter's 14 maps to the nearest token).
 *  - Pill background uses `theme.secondary` and text color uses
 *    `theme.text.primary` for consistency with the rest of the chat chrome
 *    (composer, headers). This is a deliberate web-only deviation from
 *    Flutter's `AppColors.backgroundColorLight` / `AppColors.white` — the
 *    project tokens keep theme-mode parity (light/dark) and align the pill
 *    surface with the surrounding chat UI rather than the Flutter palette.
 *  - Vertical padding uses `responsive.spacing.base` (= 12px, exact parity).
 *  - Horizontal padding: Flutter switches between 40px (short content) and
 *    12px (long content). Web uses a single `responsive.spacing.base` for
 *    both sides — the dynamic-padding behavior was an `AutoSizeText` hack
 *    that the web stack handles naturally via flex centering. Marked as a
 *    deliberate UI deviation.
 *  - Font uses `responsive.font.lg` (= 16 on mobile). Flutter uses 15pt
 *    `AutoSizeText` with `minFontSize: 15`; the responsive `lg` token keeps
 *    typography on the project's scale per `presentation.md`.
 *
 * l10n: `chat.unread.gift|photo|video` (each takes a `{peerFirstName}`
 *       placeholder). Sourced from `useLocale().l10n.chat.unread.*`.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ScrolledUpButton } from './ScrolledUpButton';

export interface UnreadBannerProps {
  /**
   * Resolved preview string for the most-recent unread peer message, or
   * `null` when there are no unread items (or the most recent item's type
   * has no preview rule). Build this with
   * `useExpauseLocalizations().expauseLocalizations.getUnreadPreview(...)`.
   * The page should derive this from the NEWEST entry in the unread
   * bucket so the pill always previews the just-arrived message (Flutter
   * `_lastUpdatedChatMessage`, `chat_screen.dart:567`).
   */
  unreadPreview: string | null;
  /**
   * Transient flag: `true` for ~3 s after every peer-message-while-
   * scrolled-away arrival, sourced from `useChat.unreadBannerVisible`.
   * Mirrors Flutter's `_unreadMessageReceived` (`chat_screen.dart:566-578`).
   * When `false`, the pill branch is skipped even if `unreadPreview` is
   * non-null — the surface falls back to `ScrolledUpButton` (or `null`
   * if `isScrolledUp` is also `false`).
   */
  unreadBannerVisible: boolean;
  /**
   * True iff the user is currently scrolled away from the bottom of the
   * list. Mirrors Flutter's `_isScrolledUp` (`chat_screen.dart:3400`).
   * Used to drive the `ScrolledUpButton` fallback when the pill branch
   * is inactive.
   */
  isScrolledUp: boolean;
  /**
   * Invoked when the user taps either branch (the pill or the
   * scroll-to-bottom button). The caller wires this to the message-list
   * "scroll to bottom" action — Flutter `_scrollDown`
   * (`chat_screen.dart:3414`, `:3452`).
   */
  onScrollDown: () => void;
  /**
   * Current unread-message count, surfaced as `data-qa-value` on the pill so
   * the qa-tester can read the count rather than parse the preview text. Test
   * affordance only — does not affect rendering. Sourced from the page's
   * already-computed `useChat.unreadCount`.
   */
  unreadCount?: number;
}

export const UnreadBanner: React.FC<UnreadBannerProps> = ({
  unreadPreview,
  unreadBannerVisible,
  isScrolledUp,
  onScrollDown,
  unreadCount,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Flutter visibility predicate (`chat_screen.dart:3399-3400`):
  //   `(_unreadMessageReceived && content != '') || _isScrolledUp`
  // The pill renders ONLY while the 3 s `unreadBannerVisible` window is open
  // AND there is a preview to show; otherwise the surface degrades to
  // `ScrolledUpButton` (visible whenever the user is scrolled away).
  const showUnreadPill = unreadBannerVisible && unreadPreview !== null;
  const showScrollDownButton = !showUnreadPill && isScrolledUp;

  if (!showUnreadPill && !showScrollDownButton) {
    return null;
  }

  return (
    <div
      style={{
        // Flutter `Align(alignment: Alignment.bottomCenter)` + `Row(mainAxis
        // Alignment.center)` — the row centers horizontally. The page owns
        // bottom placement (Flutter's bottom padding lives on the parent).
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // Don't intercept clicks on surrounding chrome; the inner element
        // re-enables pointer events on its own surface.
        pointerEvents: 'none',
      }}
    >
      {showUnreadPill ? (
        <button
          type="button"
          {...qaAttr({ id: 'chat-unread-banner', value: unreadCount })}
          onClick={onScrollDown}
          style={{
            // Flutter `ConstrainedBox(maxWidth: width / 2)` —
            // chat_screen.dart:3417.
            maxWidth: '50vw',
            backgroundColor: theme.secondary,
            color: theme.text.primary,
            // Flutter `BorderRadius.all(Radius.circular(14.0))` —
            // chat_screen.dart:3422. Closest responsive token is `lg` (16).
            borderRadius: responsive.borderRadius.lg,
            paddingTop: responsive.spacing.base,
            paddingBottom: responsive.spacing.base,
            paddingLeft: responsive.spacing.base,
            paddingRight: responsive.spacing.base,
            border: 'none',
            textAlign: 'center',
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.regular,
            cursor: 'pointer',
            // Two-line clamp matches Flutter `AutoSizeText(maxLines: 2,
            // overflow: TextOverflow.ellipsis)` — chat_screen.dart:3433-3435.
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            pointerEvents: 'auto',
          }}
        >
          {unreadPreview}
        </button>
      ) : (
        // Re-enable pointer events on the button wrapper so the round
        // surface is clickable while the row continues to ignore clicks
        // around it.
        <div style={{ pointerEvents: 'auto' }}>
          <ScrolledUpButton onClick={onScrollDown} />
        </div>
      )}
    </div>
  );
};
